import { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { collection, onSnapshot, doc, getDoc, getDocs, query, where, orderBy, limit as fbLimit, addDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth } from '../contexts/AuthContext';
import { createPortalUser } from '../utils/createUser';
import { findMatchingFixtures } from '../utils/laligaFixtures';
import { getCurrentWeekRates, toGBP } from '../utils/exchangeRates';
import { HOTELS, ROOM_TYPE_LABELS, BOARD_TYPE_LABELS, getHotelRate, isHotelAvailableOn } from '../utils/quoteHotelRates';

const STEP_LABELS = ['Travel', 'Details', 'Extras', 'Quote'];
const MAX_STEP = 4;

// This page prices everything the same way the "Online Quote" price code
// does: customer-facing figures are all £, even though MST's own hotel/
// fixture/training costs are tracked in € internally (that price code's
// items carry the real numbers — see PRICE_CODE_NAME below).
const gbp = (n) => '£' + Math.round(n || 0).toLocaleString('en-GB');

const FLOWS_DOC_PATH = ['settings', 'englishClubCrmFlows'];
const DEFAULT_QUOTE_FLOW = 'Web Quote';

const PRICE_CODE_NAME = 'Online Quote';
// Fixtures/Training Sessions start at 0 on the instant quote — staff add
// them properly on the Quote tab's own "What's Included" picker once the
// lead is in the CRM, rather than the estimate assuming a quantity.
const DEFAULT_SESSION_QTY = 0;
// Categories on the price code that are optional, customer-ticked add-ons
// (as opposed to Hotel & Food / Flights / Transport / Fixtures / Training
// Sessions / Stadium Tour, which are always part of the base package).
const OPTIONAL_EXTRA_CATEGORIES = ['Padel', 'Yoga', 'Waterpark', 'Walking Up Castle Santa Barabara'];
// Padel and Yoga are booked once per squad, not per traveller (confirmed
// with MST) — same model as Fixtures/Training Sessions, unlike the other
// optional extras which are genuinely priced per person.
// Priced per session booked (e.g. 2 sessions of ~20 people each for a bigger
// group) — independent of squads, which only affects Fixtures/Training Sessions.
const SESSION_BASED_EXTRA_CATEGORIES = ['Padel', 'Yoga'];

const AIRLINE_GROUP_LABEL = {
  Jet2: 'Jet2',
  Ryanair: 'Ryanair',
  self_booked_ryanair: 'Ryanair',
  self_booked_easyjet: 'EasyJet',
  self_booked: 'Ryanair / EasyJet', // fallback only, if a code we don't recognise shows up
};

const MONTH_WORDS = ['jan', 'feb', 'mar', 'apr', 'may', 'jun', 'jul', 'aug', 'sep', 'sept', 'oct', 'nov', 'dec'];
function weekendLabel(dateLabel, month, year) {
  // Some weekends straddle a month boundary (e.g. "28th-1st Sept") and the
  // date label already names the month — don't repeat it (was producing
  // "28th-1st Sept August 2026").
  const alreadyHasMonth = MONTH_WORDS.some((m) => dateLabel.toLowerCase().includes(m));
  return alreadyHasMonth ? `${dateLabel} ${year}` : `${dateLabel} ${month} ${year}`;
}

// Readable label for a plain 'YYYY-MM-DD' custom/midweek departure date.
function formatCustomDateLabel(iso) {
  if (!iso) return '';
  const d = new Date(iso + 'T00:00:00');
  return d.toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
}

function selfBookedAirport(route) {
  const dep = (route || '').slice(0, 3).toUpperCase();
  if (dep === 'MAN') return 'Manchester';
  if (dep === 'LIV') return 'Liverpool';
  return null;
}
function recordAirport(r) {
  if (r.selfBooked) return selfBookedAirport(r.route);
  if (r.region === 'manchester') return 'Manchester';
  if (r.region === 'belfast') return 'Belfast';
  if (r.region === 'dublin') return 'Dublin';
  return null;
}
// Self-booked weeks record the actual outbound/inbound carrier combo (e.g.
// "RY/RY", "EASY/RY") since LCC group flights can mix airlines leg to leg.
// We split by the outbound leg so Ryanair and EasyJet show as distinct,
// selectable options rather than one blended "Ryanair / EasyJet" tile.
function recordAirlineGroup(r) {
  if (!r.selfBooked) return r.airline;
  const outbound = (r.airline || '').split('/')[0].trim().toUpperCase();
  if (outbound === 'RY') return 'self_booked_ryanair';
  if (outbound === 'EASY') return 'self_booked_easyjet';
  return 'self_booked';
}

function Check({ className = 'w-3.5 h-3.5 text-white' }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
    </svg>
  );
}

function Tile({ selected, onClick, className = '', children, dotSize = 'w-6 h-6' }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`tile text-left rounded-2xl border-2 ${selected ? 'border-pb bg-[#EBF2FC]' : 'border-border bg-white hover:border-[#C7D3E3]'} relative transition-all ${className}`}
    >
      {selected && (
        <span className={`absolute top-3 right-3 ${dotSize} rounded-full bg-pb flex items-center justify-center z-10`}>
          <Check />
        </span>
      )}
      {children}
    </button>
  );
}

function StepHeader({ n, title, sub }) {
  return (
    <div className="mb-6">
      <div className="text-[11px] font-bold uppercase text-pb mb-2" style={{ letterSpacing: '0.18em' }}>Step {n} of {MAX_STEP}</div>
      <h2 className="font-extrabold text-[26px] sm:text-[30px] text-navy" style={{ letterSpacing: '-0.025em', lineHeight: 1.05 }}>{title}</h2>
      {sub && <p className="text-slate-500 text-[15px] mt-2">{sub}</p>}
    </div>
  );
}

function SummaryRow({ label, sub, value, unit, muted }) {
  return (
    <div className="flex items-start justify-between gap-3">
      <div className="min-w-0">
        <div className="font-semibold text-navy">{label}</div>
        {sub && <div className="text-[12px] text-slate-400 truncate">{sub}</div>}
      </div>
      <div className={`text-right whitespace-nowrap ${muted ? 'text-slate-400' : 'text-navy font-bold'}`}>
        {value}{unit && <span className="text-[11px] font-medium text-slate-400"> {unit}</span>}
      </div>
    </div>
  );
}

function QtyStepper({ label, value, onChange, min = 0, max = 5 }) {
  return (
    <div>
      <label className="block text-[12px] font-semibold text-slate-500 mb-1.5">{label}</label>
      <div className="flex items-center border border-border rounded-xl overflow-hidden bg-surface">
        <button type="button" onClick={() => onChange(Math.max(min, value - 1))} className="w-10 h-10 flex items-center justify-center text-navy font-bold text-lg hover:bg-white transition-colors">−</button>
        <span className="flex-1 text-center font-bold text-navy text-[16px]">{value}</span>
        <button type="button" onClick={() => onChange(Math.min(max, value + 1))} className="w-10 h-10 flex items-center justify-center text-navy font-bold text-lg hover:bg-white transition-colors">+</button>
      </div>
    </div>
  );
}

// Staff-only: search an existing portal customer to attach this quote to, or
// create a new one — same search-or-create pattern as NewRequest.js and
// CrmDatabase.js's "Convert to Tour" modal (users collection, role=customer).
function ContactAttachPanel({
  contactMode, setContactMode, customers, customerSearch, setCustomerSearch,
  showCustomerDropdown, setShowCustomerDropdown, selectedCustomer, setSelectedCustomer,
  newCustomer, setNewCustomer, creatingCustomer, customerError, onCreateCustomer,
  onSelectCustomer,
}) {
  const filtered = customers.filter((c) => {
    const q = customerSearch.toLowerCase();
    return c.email?.toLowerCase().includes(q) || c.name?.toLowerCase().includes(q);
  }).slice(0, 8);

  function selectCustomer(c) {
    setSelectedCustomer(c);
    setCustomerSearch(c.email);
    setShowCustomerDropdown(false);
    onSelectCustomer?.(c);
  }

  return (
    <div className="rounded-2xl border border-border bg-white shadow-card p-6 sm:p-7 mb-5">
      <div className="flex items-center justify-between mb-3">
        <label className="block text-[13.5px] font-bold text-navy">Attach to a portal contact <span className="font-normal text-slate-400">(optional)</span></label>
        <div className="flex gap-2">
          <button type="button" onClick={() => setContactMode('existing')}
            className={`text-[11px] px-3 py-1 rounded-[6px] font-semibold transition ${contactMode === 'existing' ? 'bg-pb text-white' : 'border border-border text-slate-500 hover:bg-surface'}`}>
            Existing contact
          </button>
          <button type="button" onClick={() => { setContactMode('new'); setSelectedCustomer(null); setCustomerSearch(''); }}
            className={`text-[11px] px-3 py-1 rounded-[6px] font-semibold transition ${contactMode === 'new' ? 'bg-pb text-white' : 'border border-border text-slate-500 hover:bg-surface'}`}>
            New contact
          </button>
        </div>
      </div>

      {contactMode === 'existing' ? (
        <div className="relative">
          <div className="relative">
            <input
              type="text" value={customerSearch}
              onChange={(e) => { setCustomerSearch(e.target.value); setSelectedCustomer(null); setShowCustomerDropdown(true); }}
              onFocus={() => setShowCustomerDropdown(true)}
              placeholder="Search portal contacts by name or email…"
              className="field w-full border border-border rounded-xl px-4 py-2.5 pr-8 text-[14px] bg-surface text-navy"
            />
            {selectedCustomer && (
              <button type="button" onClick={() => { setSelectedCustomer(null); setCustomerSearch(''); }} className="absolute right-3 top-2.5 text-slate-400 hover:text-navy text-[12px]">✕</button>
            )}
          </div>
          {showCustomerDropdown && customerSearch && !selectedCustomer && (
            <div className="absolute z-10 mt-1 w-full bg-white border border-border rounded-xl shadow-card overflow-hidden">
              {filtered.length === 0
                ? <p className="px-4 py-3 text-[13px] text-slate-400">No contacts found.</p>
                : filtered.map((c) => (
                  <button key={c.uid} type="button" onClick={() => selectCustomer(c)} className="w-full text-left px-4 py-2.5 hover:bg-surface transition">
                    <p className="text-[13px] font-semibold text-navy">{c.name || c.email}</p>
                    {c.name && <p className="text-[12px] text-slate-400">{c.email}</p>}
                  </button>
                ))}
            </div>
          )}
          {selectedCustomer && (
            <div className="mt-2 flex items-center gap-2 text-[13px] text-sg bg-green-light border border-sg/20 rounded-xl px-3 py-2">
              <span className="w-2 h-2 rounded-full bg-sg flex-shrink-0" />
              <span className="font-semibold text-navy">{selectedCustomer.name || selectedCustomer.email}</span>
              {selectedCustomer.name && <span>· {selectedCustomer.email}</span>}
            </div>
          )}
        </div>
      ) : (
        <div className="space-y-4">
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-[12px] font-semibold text-slate-500 mb-1.5">Contact name</label>
              <input type="text" value={newCustomer.name} onChange={(e) => setNewCustomer({ ...newCustomer, name: e.target.value })} placeholder="e.g. John Smith" className="field w-full border border-border rounded-xl px-3 py-2.5 text-[14px] bg-surface text-navy" />
            </div>
            <div>
              <label className="block text-[12px] font-semibold text-slate-500 mb-1.5">Email</label>
              <input type="email" value={newCustomer.email} onChange={(e) => setNewCustomer({ ...newCustomer, email: e.target.value })} placeholder="e.g. john@school.ac.uk" className="field w-full border border-border rounded-xl px-3 py-2.5 text-[14px] bg-surface text-navy" />
            </div>
          </div>
          <div>
            <label className="block text-[12px] font-semibold text-slate-500 mb-1.5">Club / school <span className="font-normal text-slate-400">(optional)</span></label>
            <input type="text" value={newCustomer.clubSchool} onChange={(e) => setNewCustomer({ ...newCustomer, clubSchool: e.target.value })} placeholder="e.g. Dixons Academy" className="field w-full border border-border rounded-xl px-3 py-2.5 text-[14px] bg-surface text-navy" />
          </div>
          <div className="flex items-center gap-3">
            <button type="button" onClick={onCreateCustomer} disabled={creatingCustomer}
              className="px-[18px] py-[9px] bg-pb hover:bg-navy text-white text-[13px] font-semibold rounded-xl disabled:opacity-60 transition">
              {creatingCustomer ? 'Creating…' : 'Create & attach contact'}
            </button>
            <p className="text-[12px] text-slate-400">They'll receive an email to set their password.</p>
          </div>
          {selectedCustomer && (
            <div className="flex items-center gap-2 text-[13px] text-sg bg-green-light border border-sg/20 rounded-xl px-3 py-2">
              <span className="w-2 h-2 rounded-full bg-sg flex-shrink-0" />
              <span className="font-semibold text-navy">{selectedCustomer.name}</span>
              <span>· {selectedCustomer.email} — account created</span>
            </div>
          )}
        </div>
      )}
      {customerError && <p className="text-[12px] text-dr mt-2">{customerError}</p>}
      <p className="text-[11px] text-slate-400 mt-3">Optional — leave blank to just submit a fresh lead, same as a customer would.</p>
    </div>
  );
}

// Per-route branding — /quote stays MST-branded (internal, staff use this
// day to day); /ytquote is the same builder wearing Your Tour's identity for
// prospects, with no pricing shown (they get a quote back from the team
// instead) and no manual flight-price box (staff-only field).
const BRAND_CONFIG = {
  mst: {
    name: 'MST Sports Tours',
    logo: '/MST Logo White.png',
    homeUrl: 'https://mstsportstours.com/',
    phone: '+34 663 265 710',
  },
  your_tour: {
    name: 'YOURTOUR',
    logo: '/YT logo v2.png',
    homeUrl: 'https://your-tour.co.uk/',
    phone: '+34 663 265 710',
  },
};

export default function Quote() {
  const location = useLocation();
  const isExternal = location.pathname === '/ytquote';
  const brandKey = isExternal ? 'your_tour' : 'mst';
  const brandConfig = BRAND_CONFIG[brandKey];
  const PRICE_DISCLAIMER = isExternal
    ? `This isn't your confirmed price. Once the ${brandConfig.name} team review your request, they'll send you your exact, confirmed quote.`
    : 'This instant quote is not your confirmed price. Once MST staff review your request, they will send you your exact, confirmed quote.';
  const { currentUser } = useAuth();
  // ── Live data ──────────────────────────────────────────────────────────
  const [priceMatrix, setPriceMatrix] = useState([]);
  const [matrixLoading, setMatrixLoading] = useState(true);
  // The "Online Quote" price code is the single source of truth for every
  // priced line except Hotel & Food and Flights, which this page overrides
  // per the customer's own selection (hotel/room/board choice, manual fare).
  const [priceCode, setPriceCode] = useState(null);
  const [settingsLoading, setSettingsLoading] = useState(true);
  // MST's hotel/fixture/training costs are tracked in € internally; the
  // customer-facing estimate is always £ — same weekly rate the rest of the
  // portal already uses to convert EUR MST-costs into GBP customer prices.
  const [fxRates, setFxRates] = useState(null);

  useEffect(() => {
    const unsub = onSnapshot(collection(db, 'priceMatrix'), (snap) => {
      setPriceMatrix(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
      setMatrixLoading(false);
    }, () => setMatrixLoading(false));
    return unsub;
  }, []);

  useEffect(() => {
    getDocs(query(collection(db, 'priceCodes'), where('name', '==', PRICE_CODE_NAME), fbLimit(1))).then((snap) => {
      setPriceCode(snap.empty ? null : { id: snap.docs[0].id, ...snap.docs[0].data() });
      setSettingsLoading(false);
    }).catch(() => setSettingsLoading(false));
  }, []);

  useEffect(() => {
    getCurrentWeekRates().then(setFxRates).catch(() => setFxRates(null));
  }, []);

  const pcItem = useCallback(
    (categoryName) => priceCode?.items?.find((i) => i.categoryName === categoryName) || null,
    [priceCode]
  );

  // CRM flows (Cold/Returner/Referral/Web Quote/…) — staff submitters pick
  // one; customer submissions are always tagged "Web Quote". Same settings
  // doc CrmDatabase.js's useFlows() reads.
  const [flows, setFlows] = useState([DEFAULT_QUOTE_FLOW]);
  useEffect(() => {
    getDoc(doc(db, ...FLOWS_DOC_PATH)).then((snap) => {
      const list = snap.exists() && Array.isArray(snap.data().flows) ? snap.data().flows : [];
      setFlows(list.length ? list : [DEFAULT_QUOTE_FLOW]);
    }).catch(() => {});
  }, []);

  // Anti-spam: records when the page mounted, and a honeypot field real
  // visitors never see — both enforced server-side in firestore.rules.
  const mountedAtRef = useRef(new Date());
  const [honeypot, setHoneypot] = useState('');

  // ── Wizard state ───────────────────────────────────────────────────────
  const [step, setStep] = useState(1);
  const [mode, setMode] = useState(null); // 'flights' | 'own'
  const [airport, setAirport] = useState(null);
  const [dateKey, setDateKey] = useState(null);
  const [airlineGroup, setAirlineGroup] = useState(null);
  const [nights, setNights] = useState(null); // 2 | 3
  const [flightOptionIdx, setFlightOptionIdx] = useState(0); // which time option, when a route has more than one
  const [flightPricePP, setFlightPricePP] = useState(''); // manually entered — never auto-computed, but feeds the total once known
  // MST-arranged flights, but for a midweek/custom date rather than one of
  // the imported weekend blocks — flight time/airline aren't known for these
  // (no matrix data to pull from), so they're always "to be confirmed" and
  // priced with the same manual flight-price box as weekend departures.
  const [flightDateMode, setFlightDateMode] = useState('weekend'); // 'weekend' | 'custom'
  const [customDate, setCustomDate] = useState('');
  const [customNights, setCustomNights] = useState(null); // 2 | 3 | 4
  const [ownDate, setOwnDate] = useState('');
  const [ownNights, setOwnNights] = useState(null);
  // Kept as the raw typed string (not coerced to a number on every
  // keystroke) so the field can actually be cleared and retyped instead of
  // snapping back to a leading "0"/"1" that new digits get stuck behind.
  // Every read coerces via Number(...) || fallback, so this is safe anywhere
  // it's used downstream.
  const [group, setGroup] = useState('16');
  // Raw typed string, same reasoning as `group` above — coerced on read.
  const [numberOfSquads, setNumberOfSquads] = useState('1');
  const [hotelKey, setHotelKey] = useState(HOTELS[0].key);
  const [boardType, setBoardType] = useState(HOTELS[0].boardTypes[0]);
  const [roomType, setRoomType] = useState(HOTELS[0].roomTypes[0]);
  const [extraIds, setExtraIds] = useState(new Set());
  const [fixtureQty, setFixtureQty] = useState(DEFAULT_SESSION_QTY);
  const [trainingQty, setTrainingQty] = useState(DEFAULT_SESSION_QTY);
  const [padelSessions, setPadelSessions] = useState(1);
  const [yogaSessions, setYogaSessions] = useState(1);
  const [submitterType, setSubmitterType] = useState('customer'); // 'customer' | 'staff'
  const [staffFlow, setStaffFlow] = useState('');
  useEffect(() => {
    if (!staffFlow && flows.length) setStaffFlow(flows[0]);
  }, [flows, staffFlow]);
  const [form, setForm] = useState({ fullName: '', email: '', groupName: '', phone: '', salesAgentName: '' });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');
  const [mobileBreakdownOpen, setMobileBreakdownOpen] = useState(false);

  // ── Attach to an existing portal contact, or create a new one — internal
  // staff use only, mirrors the same search-or-create pattern NewRequest.js
  // and CrmDatabase.js's "Convert to Tour" already use (users collection,
  // role='customer'). Never rendered/fetched on the external /ytquote page.
  const [customers, setCustomers] = useState([]);
  const [contactMode, setContactMode] = useState('existing'); // 'existing' | 'new'
  const [customerSearch, setCustomerSearch] = useState('');
  const [showCustomerDropdown, setShowCustomerDropdown] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [newCustomer, setNewCustomer] = useState({ name: '', email: '', clubSchool: '' });
  const [creatingCustomer, setCreatingCustomer] = useState(false);
  const [customerError, setCustomerError] = useState('');

  useEffect(() => {
    if (isExternal) return;
    getDocs(query(collection(db, 'users'), where('role', '==', 'customer')))
      .then((snap) => setCustomers(snap.docs.map((d) => ({ uid: d.id, ...d.data() }))))
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Pre-fills the visible form fields from an existing contact so staff
  // don't have to re-type what's already on file. name/email/clubSchool
  // live on the contact's own account (users doc); phone doesn't — that's
  // only ever recorded per tour — so it's backfilled (best-effort) from
  // their most recent past request, along with groupName if the account
  // itself has no clubSchool set. Never overwrites anything staff already
  // typed into the field.
  async function prefillFromCustomer(c) {
    setForm((f) => ({
      ...f,
      fullName: f.fullName || c.name || '',
      email: f.email || c.email || '',
      groupName: f.groupName || c.clubSchool || '',
    }));
    try {
      const snap = await getDocs(query(
        collection(db, 'requests'),
        where('customerId', '==', c.uid),
        orderBy('createdAt', 'desc'),
        fbLimit(1)
      ));
      if (snap.empty) return;
      const last = snap.docs[0].data();
      setForm((f) => ({
        ...f,
        phone: f.phone || last.phone || '',
        groupName: f.groupName || last.groupName || '',
      }));
    } catch (_) { /* best-effort only — the visible pre-fill above already ran */ }
  }

  async function handleCreateCustomer() {
    if (!newCustomer.name.trim() || !newCustomer.email.trim()) {
      setCustomerError('Name and email are required.');
      return;
    }
    setCreatingCustomer(true);
    setCustomerError('');
    try {
      const { uid } = await createPortalUser({
        email: newCustomer.email,
        role: 'customer',
        name: newCustomer.name,
        clubSchool: newCustomer.clubSchool,
        createdByEmail: currentUser?.email || '',
        brand: brandKey,
      });
      const c = {
        uid, email: newCustomer.email.trim().toLowerCase(), name: newCustomer.name.trim(),
        ...(newCustomer.clubSchool.trim() ? { clubSchool: newCustomer.clubSchool.trim() } : {}),
      };
      setCustomers((prev) => [...prev, c]);
      setSelectedCustomer(c);
      setContactMode('existing');
      setCustomerSearch(c.email);
      setNewCustomer({ name: '', email: '', clubSchool: '' });
      prefillFromCustomer(c);
    } catch (err) {
      setCustomerError(err.code === 'auth/email-already-in-use'
        ? 'An account with this email already exists.'
        : 'Failed to create contact. Please try again.');
    } finally {
      setCreatingCustomer(false);
    }
  }

  // ── Group-size pricing ratio ─────────────────────────────────────────────
  // MST prices each season twice: for 45 paying + 3 free and for 30 paying +
  // 2 free (fewer payers to spread the free places over, so higher pp). The
  // quote uses whichever ratio is nearest the entered group size — 48 and 32
  // total travellers respectively, so the midpoint is 40.
  const totalTravellers = Math.max(0, Number(group) || 0);
  const ratio = totalTravellers >= 40 ? '45/3' : '30/2';
  const ratioLabel = ratio === '45/3' ? '45 paying + 3 free places' : '30 paying + 2 free places';
  const ratioMatrix = useMemo(
    // Records without a ratio tag are from an old-style single-tab import
    () => priceMatrix.filter((r) => !r.ratio || r.ratio === ratio),
    [priceMatrix, ratio]
  );

  // ── Derived: airports available from imported flight data ──────────────
  const airportOptions = useMemo(() => {
    const set = new Set();
    ratioMatrix.forEach((r) => { const a = recordAirport(r); if (a) set.add(a); });
    return Array.from(set);
  }, [ratioMatrix]);

  // ── Derived: date options for the selected airport, each carrying the
  // airline(s) that actually fly that specific week nested underneath ─────
  const dateOptions = useMemo(() => {
    if (!airport) return [];
    const map = new Map(); // dateKey -> { key, label, sortIndex, airlines: Map }
    ratioMatrix.forEach((r) => {
      if (recordAirport(r) !== airport) return;
      const dKey = `${r.month}-${r.year}-${r.dateLabel}`;
      if (!map.has(dKey)) {
        map.set(dKey, {
          key: dKey, label: weekendLabel(r.dateLabel, r.month, r.year),
          month: r.month, year: r.year, dateLabel: r.dateLabel,
          sortIndex: r.sortIndex, airlines: new Map(),
        });
      }
      const dateEntry = map.get(dKey);
      const gKey = recordAirlineGroup(r);
      if (!dateEntry.airlines.has(gKey)) {
        dateEntry.airlines.set(gKey, {
          key: gKey, label: AIRLINE_GROUP_LABEL[gKey] || gKey, selfBooked: !!r.selfBooked,
          record: r.selfBooked ? r : null, byNights: {},
        });
      }
      if (!r.selfBooked) {
        const a = dateEntry.airlines.get(gKey);
        // Some routes (e.g. Dublin) have more than one flight-time option at
        // the same night count — keep all of them rather than overwriting.
        a.byNights[r.nights] = [...(a.byNights[r.nights] || []), r];
      }
    });
    return Array.from(map.values()).sort((a, b) => a.sortIndex - b.sortIndex);
  }, [ratioMatrix, airport]);

  const selectedDate = useMemo(() => dateOptions.find((d) => d.key === dateKey) || null, [dateOptions, dateKey]);

  // ── Departure weekends grouped by month, each carrying the attendable
  // (Elche/Valencia/Levante home) fixtures so customers can pick by match ──
  const monthGroups = useMemo(() => {
    const list = [];
    const byMonth = new Map();
    dateOptions.forEach((d) => {
      const startDay = parseInt(d.dateLabel, 10);
      let homeMatches = [], intlBreak = false;
      if (startDay) {
        const monthIdx = new Date(`${d.month} 1, ${d.year}`).getMonth();
        const mds = findMatchingFixtures(new Date(d.year, monthIdx, startDay), 3);
        intlBreak = mds.length === 0;
        homeMatches = mds.flatMap((md) => md.matches.filter((m) => m.highlight));
      }
      const mKey = `${d.month} ${d.year}`;
      if (!byMonth.has(mKey)) {
        const g = { key: mKey, month: d.month, year: d.year, options: [] };
        byMonth.set(mKey, g);
        list.push(g);
      }
      byMonth.get(mKey).options.push({ ...d, homeMatches, intlBreak });
    });
    return list;
  }, [dateOptions]);

  const [dateMonthKey, setDateMonthKey] = useState(null);
  useEffect(() => {
    if (!monthGroups.length) return;
    if (dateMonthKey && monthGroups.some((g) => g.key === dateMonthKey)) return;
    // Default to the month of the current selection, else the first month
    const sel = dateKey ? monthGroups.find((g) => g.options.some((o) => o.key === dateKey)) : null;
    setDateMonthKey((sel || monthGroups[0]).key);
  }, [monthGroups, dateMonthKey, dateKey]);
  const activeMonthGroup = monthGroups.find((g) => g.key === dateMonthKey) || null;

  const airlineOptionsForDate = useMemo(
    () => (selectedDate ? Array.from(selectedDate.airlines.values()) : []),
    [selectedDate]
  );

  // Auto-select the airline when there's only one option for this date
  useEffect(() => {
    if (selectedDate && airlineOptionsForDate.length === 1 && airlineGroup !== airlineOptionsForDate[0].key) {
      setAirlineGroup(airlineOptionsForDate[0].key);
    }
  }, [selectedDate, airlineOptionsForDate, airlineGroup]);

  const selectedAirlineEntry = airlineOptionsForDate.find((a) => a.key === airlineGroup) || null;
  const isSelfBooked = !!selectedAirlineEntry?.selfBooked;

  const flightOptions = !isSelfBooked && selectedAirlineEntry ? (selectedAirlineEntry.byNights?.[nights] || []) : [];
  const availableFlightOptions = flightOptions.filter((o) => o.flightAvailable);
  const airlineLabel = selectedAirlineEntry?.label || null;

  // ── Check-in date + hotel accommodation ───────────────────────────────────
  const isCustomFlightDates = mode === 'flights' && flightDateMode === 'custom';
  // Self-booked weekends are always a 2-night Sat-Mon trip (there's no
  // Duration picker for them — nights never gets set from a tile click).
  const activeNights = mode === 'own' ? ownNights : isCustomFlightDates ? customNights : isSelfBooked ? 2 : nights;

  // Flight-mode dates come from the imported price matrix as a weekend
  // label ("28th", sometimes a range like "28th-1st") rather than a clean
  // ISO date — pull the numeric start day the same way fixturesForDate
  // already does below, and build an ISO date from it. Midweek/custom dates
  // (flightDateMode === 'custom') already come from a plain date input.
  const checkInDate = useMemo(() => {
    if (mode === 'own') return ownDate || null;
    if (isCustomFlightDates) return customDate || null;
    if (mode === 'flights' && selectedDate) {
      const startDay = parseInt(selectedDate.dateLabel, 10);
      if (!startDay) return null;
      const monthIdx = new Date(`${selectedDate.month} 1, ${selectedDate.year}`).getMonth();
      return new Date(selectedDate.year, monthIdx, startDay).toISOString().slice(0, 10);
    }
    return null;
  }, [mode, ownDate, selectedDate, isCustomFlightDates, customDate]);

  // The portal's cost-sheet engine (RequestDetail.js) always computes nights
  // from departureDate/returnDate, never from a standalone "nights" field —
  // without these, every web-quote tour silently falls back to 1 night there,
  // no matter how many nights the customer actually selected here.
  const returnDate = useMemo(() => {
    if (!checkInDate || !activeNights) return null;
    const d = new Date(checkInDate + 'T00:00:00');
    d.setDate(d.getDate() + activeNights);
    return d.toISOString().slice(0, 10);
  }, [checkInDate, activeNights]);

  const hotel = HOTELS.find((h) => h.key === hotelKey) || HOTELS[0];
  const hotelRate = getHotelRate(hotelKey, boardType, roomType, checkInDate, activeNights, totalTravellers);
  const hotelName = hotel.name;

  // Some hotels (La Nucia) are only priced for a handful of specific
  // fixture-weekend dates rather than a continuous calendar — once a date's
  // picked, only offer hotels that actually have pricing for it instead of
  // showing an option that just says "No pricing for this date".
  const availableHotels = useMemo(
    () => HOTELS.filter((h) => isHotelAvailableOn(h.key, checkInDate, activeNights, totalTravellers)),
    [checkInDate, activeNights, totalTravellers]
  );
  useEffect(() => {
    if (!checkInDate || availableHotels.length === 0) return;
    if (availableHotels.some((h) => h.key === hotelKey)) return;
    selectHotel(availableHotels[0].key);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [checkInDate, availableHotels]);

  // ── La Liga fixtures for the selected departure/check-in week ────────────
  const fixturesForDate = useMemo(() => {
    if (isCustomFlightDates && customDate) {
      return findMatchingFixtures(new Date(customDate + 'T00:00:00'), customNights || 3);
    }
    if (mode === 'flights' && dateKey && airport) {
      const rec = priceMatrix.find(
        (r) => recordAirport(r) === airport && `${r.month}-${r.year}-${r.dateLabel}` === dateKey
      );
      if (!rec) return [];
      const startDay = parseInt(rec.dateLabel, 10);
      if (!startDay) return [];
      const monthIdx = new Date(`${rec.month} 1, ${rec.year}`).getMonth();
      return findMatchingFixtures(new Date(rec.year, monthIdx, startDay), nights || 3);
    }
    if (mode === 'own' && ownDate) {
      return findMatchingFixtures(new Date(ownDate + 'T00:00:00'), ownNights || 3);
    }
    return [];
  }, [mode, dateKey, airport, priceMatrix, nights, ownDate, ownNights, isCustomFlightDates, customDate, customNights]);

  // ── Pricing ──────────────────────────────────────────────────────────────
  // Matches how every other tour's cost sheet is built from a price code
  // (see buildStaticCostSheet/recalculateTotals in staff/RequestDetail.js):
  // each category's cost is computed against the full headcount (everyone,
  // including free places, actually stays in the hotel / takes the stadium
  // tour), then the grand total is divided across paying travellers only —
  // free places don't pay, so their share is absorbed by everyone else.
  //
  // Hotel & Food's MST cost (tracked in €) gets marked up by the price
  // code's own targetMargin before converting to £ — confirmed against its
  // Flights line (MST £199.97 → customer £240, exactly ×1.20) that this
  // field is applied as a straight markup on cost, not a margin-on-price
  // formula, despite the name. Hoisted out of calc so the room-tile prices
  // (step 2) show the same marked-up figure the total actually uses.
  const markupMultiplier = 1 + (Number(priceCode?.targetMargin) || 20) / 100;

  const calc = useMemo(() => {
    const headcount = totalTravellers;
    // One free place per 15 paying travellers: 15 paying bring 1 free (16 on
    // tour), 30 bring 2 (32), 45 bring 3 (48) — the exact same formula the
    // Quote tab uses, staff can override it there once this is in the CRM.
    const freeCount = Math.floor(headcount / 16);
    const payingCount = Math.max(1, headcount - freeCount);

    // Hotel & Food — overridden by the hotel/room/board picker.
    const hotelEUR = hotelRate.available
      ? (hotelRate.isTotal ? hotelRate.perPerson : hotelRate.perPerson * (activeNights || 0))
      : 0;
    const hotelGBP = fxRates ? toGBP(hotelEUR, 'EUR', fxRates) * markupMultiplier : 0;

    // Flights — never auto-computed, and never marked up either: whatever
    // real fare someone types in is charged exactly as entered.
    const flightPP = mode === 'flights' ? (Number(flightPricePP) || 0) : 0;

    // Transport & Stadium Tour — straight from the price code, no overrides.
    const transportTotal = Number(pcItem('Transport')?.costToCustomer) || 0;
    const stadiumPP = Number(pcItem('Stadium Tour')?.costToCustomer) || 0;

    // Fixtures & Training Sessions — the price code's figure is per fixture/
    // session, per squad (confirmed with MST — not a flat one-off).
    const fixtureUnit = Number(pcItem('Fixtures')?.costToCustomer) || 0;
    const trainingUnit = Number(pcItem('Training Sessions')?.costToCustomer) || 0;
    const squads = Math.max(1, Number(numberOfSquads) || 1);
    const fixturesTotal = fixtureUnit * fixtureQty * squads;
    const trainingTotal = trainingUnit * trainingQty * squads;

    // Optional extras — customer-ticked, straight from the price code. Most
    // are priced per person (Waterpark, the castle walk); Padel and Yoga are
    // a flat cost per SESSION booked (e.g. 2 sessions of ~20 people each for
    // a bigger group) — independent of squads, unlike Fixtures/Training above.
    const sessionsFor = (name) => {
      if (name === 'Padel') return Math.max(1, Number(padelSessions) || 1);
      if (name === 'Yoga') return Math.max(1, Number(yogaSessions) || 1);
      return 1;
    };
    const extraItems = OPTIONAL_EXTRA_CATEGORIES.map((name) => pcItem(name)).filter(Boolean);
    const selectedExtras = extraItems.filter((i) => extraIds.has(i.categoryName));
    const perPersonExtras = selectedExtras.filter((i) => !SESSION_BASED_EXTRA_CATEGORIES.includes(i.categoryName));
    const sessionBasedExtras = selectedExtras.filter((i) => SESSION_BASED_EXTRA_CATEGORIES.includes(i.categoryName));
    const extrasPP = perPersonExtras.reduce((s, i) => s + (Number(i.costToCustomer) || 0), 0);
    const extrasFlatTotal = sessionBasedExtras.reduce((s, i) => s + (Number(i.costToCustomer) || 0) * sessionsFor(i.categoryName), 0);

    const total =
      (hotelGBP + flightPP + stadiumPP + extrasPP) * headcount
      + transportTotal + fixturesTotal + trainingTotal + extrasFlatTotal;
    const perPerson = total / payingCount;

    return {
      totalTravellers, freeCount, payingCount, activeNights, headcount,
      hotelGBP, hotelCostEUR: hotelEUR, hotelAvailable: hotelRate.available, hotelUnavailableReason: hotelRate.reason,
      flightPP, transportTotal, stadiumPP,
      fixtureUnit, trainingUnit, fixturesTotal, trainingTotal,
      extras: selectedExtras.map((i) => ({
        id: i.categoryName, name: i.categoryName, price: Number(i.costToCustomer) || 0,
        sessionBased: SESSION_BASED_EXTRA_CATEGORIES.includes(i.categoryName),
        sessions: SESSION_BASED_EXTRA_CATEGORIES.includes(i.categoryName) ? sessionsFor(i.categoryName) : null,
      })),
      extrasPP, extrasFlatTotal,
      perPerson, total,
    };
  }, [totalTravellers, activeNights, pcItem, markupMultiplier, extraIds, hotelRate, fxRates, trainingQty, fixtureQty, numberOfSquads, mode, flightPricePP, padelSessions, yogaSessions]);

  const perPersonDisplay = calc.perPerson > 0 ? gbp(calc.perPerson) : '—';
  const totalDisplay = calc.total > 0 ? gbp(calc.total) : '—';

  // ── Validation ────────────────────────────────────────────────────────────
  function stepValid(n) {
    switch (n) {
      case 1: return !!mode;
      case 2: {
        if (calc.totalTravellers < 1) return false;
        // Hotel isn't customer-picked externally — MST assigns it in the CRM
        // afterward, so an unpriced date for the auto-selected hotel
        // shouldn't block submission (there's no hotel UI here to explain why).
        if (!isExternal && !calc.hotelAvailable) return false;
        if (mode === 'own') return !!ownDate && !!ownNights;
        if (mode === 'flights') {
          if (!airport) return false;
          if (flightDateMode === 'custom') return !!customDate && !!customNights;
          if (!dateKey || !airlineGroup) return false;
          if (isSelfBooked) return true;
          return !!nights && availableFlightOptions.length > 0;
        }
        return false;
      }
      case 3: return true;
      case 4: return true;
      default: return true;
    }
  }
  function stepValidUpTo(n) {
    for (let i = 1; i <= n; i++) if (!stepValid(i)) return false;
    return true;
  }

  function goNext() { if (step < MAX_STEP && stepValid(step)) setStep((s) => s + 1); }
  function goBack() { if (step > 1) setStep((s) => s - 1); }
  function jumpStep(n) { if (n <= step || stepValidUpTo(n - 1)) setStep(n); }

  function selectMode(m) {
    setMode(m);
    setAirport(null); setDateKey(null); setAirlineGroup(null); setNights(null); setFlightOptionIdx(0); setFlightPricePP('');
    setFlightDateMode('weekend'); setCustomDate(''); setCustomNights(null);
  }
  function selectAirport(a) {
    setAirport(a);
    setDateKey(null); setAirlineGroup(null); setNights(null); setFlightOptionIdx(0); setFlightPricePP('');
    setFlightDateMode('weekend'); setCustomDate(''); setCustomNights(null);
  }
  function selectDate(key) {
    setDateKey(key); setAirlineGroup(null); setNights(null); setFlightOptionIdx(0);
  }
  function selectAirline(key) {
    setAirlineGroup(key); setNights(null); setFlightOptionIdx(0);
  }
  function selectNights(n) {
    setNights(n); setFlightOptionIdx(0);
  }
  function selectHotel(key) {
    const h = HOTELS.find((x) => x.key === key);
    setHotelKey(key);
    setBoardType(h.boardTypes[0]);
    setRoomType(h.roomTypes[0]);
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setError('');
    if (honeypot) return; // silently drop — bot filled a field real users never see
    if (!form.fullName.trim() || !form.email.trim() || !form.phone.trim() || !form.groupName.trim()) {
      setError('Please fill in all required fields.');
      return;
    }
    if (submitterType === 'staff' && !form.salesAgentName.trim()) {
      setError('Please add your name as the MST staff member raising this quote.');
      return;
    }
    setSubmitting(true);
    try {
      const resolvedCrmFlow = submitterType === 'staff' ? (staffFlow || DEFAULT_QUOTE_FLOW) : DEFAULT_QUOTE_FLOW;
      await addDoc(collection(db, 'requests'), {
        source: 'instant_quote',
        submittedBy: submitterType,
        leaderCostBasis: 'days', // Tour/Junior Lead costed by calendar days present, not nights stayed
        ...(submitterType === 'staff' ? { salesAgentName: form.salesAgentName.trim() } : {}),
        status: 'submitted',
        category: 'sales_admin',
        archived: false,
        stageProgress: {},
        customerId: (!isExternal && submitterType === 'staff' && selectedCustomer) ? selectedCustomer.uid : null,
        contactName: form.fullName.trim(),
        customerEmail: form.email.trim(),
        // Server-side merge key (onRequestCreated in functions/index.js) —
        // lets repeat submissions from the same customer fold onto one CRM
        // lead regardless of email casing, without the client ever being
        // able to look up/read another lead itself (see firestore.rules).
        customerEmailLower: form.email.trim().toLowerCase(),
        phone: form.phone.trim(),
        groupName: form.groupName.trim(),
        internalRef: form.groupName.trim(),
        destination: 'Alicante (Costa Blanca)',
        sport: 'Football',
        hotelName, hotelKey, boardType, roomType,
        numberOfSquads: Math.max(1, Number(numberOfSquads) || 1), fixtureQty, trainingQty,
        padelSessions: Math.max(1, Number(padelSessions) || 1),
        yogaSessions: Math.max(1, Number(yogaSessions) || 1),
        travelMode: mode,
        ...(mode === 'flights' ? {
          region: airport,
          regionLabel: isCustomFlightDates ? `${airport} (custom dates)` : `${airport} (${airlineLabel})`,
          weekendLabel: isCustomFlightDates ? formatCustomDateLabel(customDate) : (selectedDate?.label || ''),
          selfBooked: isSelfBooked,
        } : { checkInDate: ownDate }),
        nights: activeNights,
        departureDate: checkInDate,
        returnDate,
        participants: calc.totalTravellers,
        freePlaces: calc.freeCount,
        payingParticipants: calc.payingCount,
        selectedAddons: calc.extras.map((x) => ({ categoryId: x.id, categoryName: x.name })),
        estimateDetails: {
          currency: 'GBP',
          perPerson: calc.perPerson,
          total: calc.total,
          hotelGBP: calc.hotelGBP,
          hotelCostEUR: calc.hotelCostEUR,
          transportTotal: calc.transportTotal,
          stadiumPP: calc.stadiumPP,
          fixturesTotal: calc.fixturesTotal,
          trainingTotal: calc.trainingTotal,
          flightPP: calc.flightPP,
          priceCodeId: priceCode?.id || null,
          // Frozen snapshot of what was actually submitted — the top-level
          // fields below (participants, numberOfSquads, fixtureQty, etc.) get
          // edited later in the CRM as the real booking firms up, so without
          // this copy there'd be no way to see what the customer's original
          // £-figure above was actually based on.
          travellers: calc.totalTravellers,
          freePlaces: calc.freeCount,
          payingParticipants: calc.payingCount,
          numberOfSquads: Math.max(1, Number(numberOfSquads) || 1),
          fixtureQty, trainingQty,
          padelSessions: Math.max(1, Number(padelSessions) || 1),
          yogaSessions: Math.max(1, Number(yogaSessions) || 1),
          hotelName, boardType, roomType,
          nights: activeNights,
          extras: calc.extras.map((x) => x.name),
        },
        createdAt: serverTimestamp(),
        honeypot: '',
        clientStartedAt: mountedAtRef.current,
        // Every /quote submission lands in the CRM, not the Tours list —
        // customers always start in "Web Quote"; staff pick the flow themselves.
        crmOrigin: true,
        crmFlow: resolvedCrmFlow,
        crmStage: 'Tour request',
        crmNoteLog: [],
        crmAssignedTo: '',
        crmAssignedToName: '',
        sourceLeadId: null,
        // TEMPORARY (per Liam): every /quote and /ytquote lead is Your Tour
        // branded for now, regardless of route or CRM flow chosen — revert
        // to `resolvedCrmFlow === DEFAULT_QUOTE_FLOW ? 'your_tour' : brandKey`
        // once MST-branded leads should flow through here again.
        brand: 'your_tour',
        market: 'uk_to_spain',
        // Same field CrmPipelinePanel's manual Flight price editor reads/writes
        // — whatever's entered here pre-fills it, staff can refine it later
        // once they've confirmed the real fare.
        ...(calc.flightPP > 0 ? { flightPriceManual: `£${calc.flightPP.toFixed(2)}pp` } : {}),
      });
      setSubmitted(true);
    } catch (err) {
      console.error(err);
      setError('Something went wrong sending your request. Please try again or contact us directly.');
    } finally {
      setSubmitting(false);
    }
  }

  // ── Summary lines (shared between desktop sidebar + mobile bar) ─────────
  // Every category here is genuinely priced — nothing is "included for free".
  function SummaryLines() {
    return (
      <div className="space-y-3 text-[14px]">
        {mode === 'flights' && (
          <SummaryRow
            label={isSelfBooked ? 'Flights' : 'Return flights'}
            sub={airport ? `${airport}${airlineLabel && !isSelfBooked ? ' · ' + airlineLabel : ''}${isSelfBooked ? ' — booked by your club/school' : ''}` : ''}
            value={calc.flightPP > 0 ? gbp(calc.flightPP) : 'To be confirmed'}
            unit={calc.flightPP > 0 ? 'pp' : ''}
            muted={calc.flightPP === 0}
          />
        )}
        {mode === 'own' && (
          <SummaryRow label="Flights" sub="arranged by you" value="Not included" muted />
        )}
        <SummaryRow
          label="Hotel & full board"
          sub={calc.activeNights ? `${hotelName} · ${BOARD_TYPE_LABELS[boardType] || ''} · ${ROOM_TYPE_LABELS[roomType] || ''} · ${calc.activeNights} nights` : ''}
          value={calc.hotelGBP > 0 ? gbp(calc.hotelGBP) : '—'}
          unit={calc.hotelGBP > 0 ? 'pp' : ''}
        />
        <SummaryRow
          label="Transport"
          sub="For the whole group, split between paying travellers"
          value={calc.payingCount > 0 ? gbp(calc.transportTotal / calc.payingCount) : gbp(0)}
          unit="pp"
        />
        <SummaryRow
          label="Stadium tour"
          value={calc.stadiumPP > 0 ? gbp(calc.stadiumPP) : '—'}
          unit={calc.stadiumPP > 0 ? 'pp' : ''}
        />
        <SummaryRow
          label="Fixtures"
          sub={`${fixtureQty} × ${numberOfSquads} squad(s)`}
          value={calc.payingCount > 0 ? gbp(calc.fixturesTotal / calc.payingCount) : gbp(0)}
          unit="pp"
        />
        <SummaryRow
          label="Training sessions"
          sub={`${trainingQty} × ${numberOfSquads} squad(s)`}
          value={calc.payingCount > 0 ? gbp(calc.trainingTotal / calc.payingCount) : gbp(0)}
          unit="pp"
        />
        <SummaryRow
          label="Extras"
          sub={(() => {
            const n = calc.extras.filter((e) => !e.sessionBased).length;
            return n ? `${n} selected` : 'none yet';
          })()}
          value={gbp(calc.extrasPP)}
          unit={calc.extrasPP > 0 ? 'pp' : ''}
        />
        {calc.extrasFlatTotal > 0 && (
          <SummaryRow
            label="Padel / Yoga"
            sub={calc.extras.filter((e) => e.sessionBased).map((e) => `${e.name} (${e.sessions} session${e.sessions === 1 ? '' : 's'})`).join(' & ')}
            value={calc.payingCount > 0 ? gbp(calc.extrasFlatTotal / calc.payingCount) : gbp(0)}
            unit="pp"
          />
        )}
      </div>
    );
  }

  function FixturesPanel({ matchdays }) {
    // Groups can only attend Elche / Valencia / Levante home games (the
    // highlighted ones) — listing the rest of the round just adds noise.
    const attendable = (matchdays || [])
      .map((md) => ({ ...md, matches: md.matches.filter((m) => m.highlight) }))
      .filter((md) => md.matches.length > 0);
    if (attendable.length === 0) return null;
    return (
      <div className="mt-5 rounded-2xl border border-[#E8C84A]/40 bg-[#FEFCE8] overflow-hidden">
        <div className="px-5 py-3 border-b border-[#E8C84A]/40 flex items-center gap-2">
          <span className="text-[15px]">⚽</span>
          <span className="font-bold text-navy text-[13.5px]">La Liga fixtures for your tour dates</span>
          <span className="ml-auto text-[11px] text-slate-400 hidden sm:block">Exact days/times confirmed ~3 weeks before each round</span>
        </div>
        <div className="divide-y divide-[#E8C84A]/30">
          {attendable.map((md) => (
            <div key={md.matchday} className="px-5 py-3">
              <div className="text-[11px] font-bold uppercase text-slate-400 mb-2" style={{ letterSpacing: '0.12em' }}>
                Matchday {md.matchday} · w/c {md.wcLabel}
              </div>
              <div className="space-y-1.5">
                {md.matches.map((m, i) => (
                  <div key={i} className="flex items-center gap-2 rounded-lg bg-[#FEF08A]/60 border border-[#E8C84A]/50 px-3 py-1.5">
                    <span className="text-[11px]">🟡</span>
                    <span className="font-bold text-navy text-[13.5px]">{m.home}</span>
                    <span className="text-slate-400 text-[12px] font-medium">vs</span>
                    <span className="text-navy text-[13.5px]">{m.away}</span>
                    <span className="ml-auto text-[10px] font-bold uppercase text-[#92400E] bg-[#FDE68A] px-1.5 py-0.5 rounded-[4px]">Home</span>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
        <div className="px-5 py-2.5 border-t border-[#E8C84A]/40 sm:hidden">
          <p className="text-[11px] text-slate-400">Exact days/times confirmed ~3 weeks before each round.</p>
        </div>
      </div>
    );
  }

  if (matrixLoading || settingsLoading) {
    return <div className="min-h-screen bg-page flex items-center justify-center text-slate-400">Loading…</div>;
  }

  return (
    <div className={`bg-page text-navy antialiased font-sans ${isExternal ? 'yt-scope' : ''}`}>
      {/* Your Tour reskin — /ytquote only. Overrides the MST navy/blue accents
          with Your Tour's real identity (black + vivid yellow #F5E303 + white,
          per your-tour.co.uk), scoped to this wrapper so /quote (internal,
          still MST-branded) is untouched. */}
      {isExternal && (
        <style>{`
          .yt-scope .bg-navy\\/95 { background-color: rgba(11,12,17,.97) !important; }
          .yt-scope .bg-navy { background-color: #0B0C11 !important; }
          .yt-scope .border-navy { border-color: #0B0C11 !important; }
          .yt-scope .text-navy { color: #0B0C11 !important; }
          .yt-scope .bg-pb\\/30 { background-color: rgba(245,227,3,.20) !important; }
          .yt-scope .bg-pb { background-color: #F5E303 !important; }
          .yt-scope .border-pb { border-color: #F5E303 !important; }
          .yt-scope .text-pb { color: #0B0C11 !important; }
          .yt-scope .bg-ba { background-color: #F5E303 !important; }
          .yt-scope .text-ba { color: #F5E303 !important; }
          .yt-scope .border-ba { border-color: #F5E303 !important; }
          .yt-scope .hover\\:bg-pb:hover { background-color: #1F2937 !important; }
          .yt-scope .hover\\:bg-navy:hover { background-color: #1F2937 !important; }
          .yt-scope .bg-\\[\\#EBF2FC\\] { background-color: #FDF9D1 !important; }
        `}</style>
      )}
      {/* HEADER */}
      <header className={`sticky top-0 z-40 border-b ${isExternal ? 'bg-[#F5E303] border-black/10' : 'bg-navy/95 backdrop-blur-md border-white/10'}`}>
        <div className="max-w-6xl mx-auto px-5 sm:px-6 h-20 flex items-center justify-between">
          <a href={brandConfig.homeUrl} className="flex items-center gap-3">
            <img src={brandConfig.logo} alt={brandConfig.name} className={isExternal ? 'h-14 w-auto' : 'h-7 w-auto'} />
          </a>
          <div className="flex items-center gap-5">
            <a href={`tel:${brandConfig.phone.replace(/\s+/g, '')}`} className={`hidden sm:flex items-center gap-2 text-[13.5px] font-semibold transition-colors ${isExternal ? 'text-[#0B0C11] hover:opacity-70' : 'text-white/70 hover:text-white'}`}>
              {brandConfig.phone}
            </a>
            <span className={`hidden sm:block w-px h-5 ${isExternal ? 'bg-black/15' : 'bg-white/15'}`} />
            <span className={`text-[11px] font-bold uppercase ${isExternal ? 'text-[#0B0C11]' : 'text-ba'}`} style={{ letterSpacing: '0.18em' }}>{isExternal ? 'Get a quote' : 'Instant quote'}</span>
          </div>
        </div>
      </header>

      {/* HERO */}
      <section className="relative bg-navy text-white overflow-hidden">
        <div className="absolute -top-28 -right-32 w-[520px] h-[520px] rounded-full bg-pb/30 blur-[120px] pointer-events-none" />
        <div className="relative max-w-6xl mx-auto px-5 sm:px-6 pt-12 pb-14 sm:pt-14 sm:pb-16">
          <span className="inline-flex items-center gap-2 text-[11px] font-bold uppercase text-ba mb-5" style={{ letterSpacing: '0.18em' }}>
            <span className="w-6 h-px bg-ba" />Build your tour
          </span>
          <h1 className="font-extrabold text-[32px] sm:text-[44px] max-w-2xl mb-4" style={{ letterSpacing: '-0.025em', lineHeight: 1.05 }}>
            {isExternal
              ? <>Tell us about your <span className="text-ba">Spanish football tour</span> and we'll send you a quote.</>
              : <>Get an instant estimate for your <span className="text-ba">Spanish football tour.</span></>}
          </h1>
          <p className="text-white/65 text-[16px] sm:text-[17px] leading-relaxed max-w-xl">
            {isExternal
              ? "Tell us what you're after in a couple of minutes — hotel, dates, extras — and our team will send you a tailored, confirmed price."
              : "Configure your trip in a couple of minutes and watch the price update as you go. When you're happy, request a firm quote and our team will confirm it."}
          </p>
        </div>
      </section>

      <main className="max-w-6xl mx-auto px-5 sm:px-6 py-10 sm:py-12">
        <div className={`grid gap-8 lg:gap-10 items-start ${isExternal ? 'max-w-3xl mx-auto' : 'lg:grid-cols-[1fr_356px]'}`}>

          {/* LEFT: stepper + steps */}
          <div>
            <ol className="flex items-center gap-2 sm:gap-3 mb-8 select-none">
              {STEP_LABELS.map((label, i) => {
                const n = i + 1;
                const done = n < step;
                const cur = n === step;
                const clickable = n <= step || stepValidUpTo(n - 1);
                return (
                  <li key={label} className={`flex items-center gap-2 ${clickable ? 'cursor-pointer group' : ''}`} onClick={() => clickable && jumpStep(n)}>
                    {done ? (
                      <span className="w-7 h-7 rounded-full bg-sg flex items-center justify-center flex-shrink-0"><Check /></span>
                    ) : (
                      <span className={`w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 text-[13px] font-bold ${cur ? 'bg-ba text-navy' : 'bg-white border border-border text-slate-400'}`}>{n}</span>
                    )}
                    <span className={`hidden sm:block text-[13px] font-semibold ${cur ? 'text-navy' : done ? 'text-sg' : 'text-slate-400'}`}>{label}</span>
                    {n < STEP_LABELS.length && <span className={`w-4 sm:w-7 h-px ${done ? 'bg-sg' : 'bg-border'} ml-1`} />}
                  </li>
                );
              })}
            </ol>

            {/* STEP 1 */}
            {step === 1 && (
              <div>
                <StepHeader n={1} title="How do you want to travel?" sub={`Choose whether ${brandConfig.name} arranges your flights too, or you'll sort your own and just need the tour on the ground.`} />
                <div className="grid sm:grid-cols-2 gap-4">
                  <Tile selected={mode === 'flights'} onClick={() => selectMode('flights')} className="p-6">
                    <span className="w-12 h-12 rounded-xl flex items-center justify-center mb-5 bg-[#EBF2FC]">
                      <svg className="w-7 h-7 text-pb" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.7}><path strokeLinecap="round" strokeLinejoin="round" d="M6 12L3.27 6.96a.6.6 0 01.54-.88l1.92.02a1.2 1.2 0 01.9.43L9 9m12 1.5a1.5 1.5 0 01-1.5 1.5H4.5L2.6 17.4a.6.6 0 00.55.85h1.6a1.2 1.2 0 00.86-.36L8.25 15H15l4.5-.01A1.5 1.5 0 0021 13.5v0a1.5 1.5 0 00-.9-1.37L9 7.5 7.5 3.6a.6.6 0 01.57-.85h1.46c.4 0 .78.2 1 .53L15 9l4.6 1.15A1.5 1.5 0 0121 11.5z" /></svg>
                    </span>
                    <h3 className="font-extrabold text-navy text-[18px] mb-1.5">Let {brandConfig.name} arrange your flights</h3>
                    <p className="text-slate-500 text-[14px] leading-relaxed">Flights, transport and your full tour, bundled together. The easiest, fully-managed option.</p>
                    <span className="inline-flex items-center gap-1.5 mt-4 text-[12px] font-bold uppercase text-ba">Flights + tour</span>
                  </Tile>
                  <Tile selected={mode === 'own'} onClick={() => selectMode('own')} className="p-6">
                    <span className="w-12 h-12 rounded-xl flex items-center justify-center mb-5 bg-[#EBF2FC]">
                      <svg className="w-7 h-7 text-pb" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.7}><path strokeLinecap="round" strokeLinejoin="round" d="M2.25 21h19.5m-18-18v18m10.5-18v18m6-13.5V21M6.75 6.75h.75m-.75 3h.75m-.75 3h.75m3-6h.75m-.75 3h.75m-.75 3h.75M6.75 21v-3.375c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125V21" /></svg>
                    </span>
                    <h3 className="font-extrabold text-navy text-[18px] mb-1.5">I'll arrange my own flights</h3>
                    <p className="text-slate-500 text-[14px] leading-relaxed">Just the tour on the ground — hotel, full board, coaching and activities. You handle the travel to Spain.</p>
                    <span className="inline-flex items-center gap-1.5 mt-4 text-[12px] font-bold uppercase text-ba">Tour only</span>
                  </Tile>
                </div>
              </div>
            )}

            {/* STEP 2 */}
            {step === 2 && (
              <div>
                <StepHeader n={2} title="Your trip details" sub="All tours are based on the Costa Blanca, flying into Alicante (ALC)." />

                {mode === 'flights' && (
                  <div className="mb-7">
                    <label className="block text-[13.5px] font-bold text-navy mb-3">Departure airport</label>
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                      {airportOptions.map((a) => (
                        <Tile key={a} selected={airport === a} onClick={() => selectAirport(a)} className="px-3 py-3 text-center" dotSize="w-4 h-4">
                          <div className="font-bold text-navy text-[14.5px] leading-tight">{a}</div>
                        </Tile>
                      ))}
                      {airportOptions.length === 0 && (
                        <p className="text-[13px] text-slate-400 col-span-full">No flight price data has been loaded yet — contact {brandConfig.name}.</p>
                      )}
                    </div>

                    {airport && (
                      <div className="mt-5">
                        <label className="block text-[13.5px] font-bold text-navy mb-2">When do you want to travel?</label>
                        <div className="grid sm:grid-cols-2 gap-3 max-w-lg">
                          <Tile selected={flightDateMode === 'weekend'} onClick={() => setFlightDateMode('weekend')} className="p-4" dotSize="w-4 h-4">
                            <div className="font-bold text-navy text-[14.5px] leading-tight pr-6">A weekend</div>
                            <p className="text-[12px] text-slate-400 mt-1">Pick from our regular scheduled weekend departures.</p>
                          </Tile>
                          <Tile selected={flightDateMode === 'custom'} onClick={() => setFlightDateMode('custom')} className="p-4" dotSize="w-4 h-4">
                            <div className="font-bold text-navy text-[14.5px] leading-tight pr-6">Specific dates</div>
                            <p className="text-[12px] text-slate-400 mt-1">Midweek or any other dates — we'll arrange the flights around it.</p>
                          </Tile>
                        </div>
                      </div>
                    )}

                    {flightDateMode === 'weekend' && airport && monthGroups.length > 0 && (
                      <div className="mt-5">
                        <label className="block text-[13.5px] font-bold text-navy mb-2">Departure month</label>
                        <div className="flex flex-wrap gap-2 mb-4">
                          {monthGroups.map((g) => (
                            <button
                              key={g.key} type="button" onClick={() => setDateMonthKey(g.key)}
                              className={`px-3.5 py-2 rounded-full text-[13px] font-bold border transition-colors ${dateMonthKey === g.key ? 'bg-navy text-white border-navy' : 'bg-white text-slate-500 border-border hover:border-[#C7D3E3] hover:text-navy'}`}
                            >
                              {g.month}<span className={`ml-1 text-[11px] font-medium ${dateMonthKey === g.key ? 'text-white/60' : 'text-slate-400'}`}>{g.year}</span>
                            </button>
                          ))}
                        </div>
                        <label className="block text-[13.5px] font-bold text-navy mb-2">Departure weekend</label>
                        <div className="grid sm:grid-cols-2 gap-3">
                          {(activeMonthGroup?.options || []).map((d) => (
                            <Tile key={d.key} selected={dateKey === d.key} onClick={() => selectDate(d.key)} className="p-4" dotSize="w-4 h-4">
                              <div className="font-bold text-navy text-[15px] pr-6">{d.dateLabel}</div>
                              <div className="mt-1.5 space-y-1">
                                {d.homeMatches.length > 0 ? d.homeMatches.slice(0, 3).map((m, i) => (
                                  <div key={i} className="text-[12px] text-slate-500 flex items-center gap-1.5">
                                    <span className="text-[10px]">⚽</span>
                                    <span className="truncate"><span className="font-semibold text-navy">{m.home}</span> v {m.away}</span>
                                  </div>
                                )) : (
                                  <div className="text-[12px] text-slate-400">
                                    {d.intlBreak ? 'International break — no La Liga this weekend' : 'No local home games this weekend'}
                                  </div>
                                )}
                              </div>
                            </Tile>
                          ))}
                        </div>
                        <p className="text-[11.5px] text-slate-400 mt-2.5">⚽ Home fixtures your group can attend (Elche, Valencia &amp; Levante). Exact days/times are confirmed ~3 weeks before each round.</p>
                      </div>
                    )}
                    {flightDateMode === 'weekend' && airport && monthGroups.length === 0 && (
                      <p className="mt-4 text-[13px] text-slate-400">No departure dates available from this airport — try another.</p>
                    )}

                    {flightDateMode === 'weekend' && selectedDate && airlineOptionsForDate.length > 1 && (
                      <div className="mt-5">
                        <label className="block text-[13.5px] font-bold text-navy mb-3">Airline</label>
                        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 max-w-md">
                          {airlineOptionsForDate.map((opt) => (
                            <Tile key={opt.key} selected={airlineGroup === opt.key} onClick={() => selectAirline(opt.key)} className="px-3 py-3 text-center" dotSize="w-4 h-4">
                              <div className="font-bold text-navy text-[14.5px] leading-tight">{opt.label}</div>
                            </Tile>
                          ))}
                        </div>
                      </div>
                    )}
                    {flightDateMode === 'weekend' && selectedDate && airlineOptionsForDate.length === 1 && (
                      <p className="mt-4 text-[13px] text-slate-500">
                        <span className="font-bold text-navy">Airline:</span> {airlineOptionsForDate[0].label} — the only option on this date.
                      </p>
                    )}
                    {flightDateMode === 'weekend' && selectedDate && airlineOptionsForDate.length === 0 && (
                      <p className="mt-4 text-[13px] text-slate-400">No flights found for this date — try another.</p>
                    )}

                    {flightDateMode === 'weekend' && selectedAirlineEntry && (
                      <div className="mt-5">
                        {!isSelfBooked && (
                          <>
                            <label className="block text-[13.5px] font-bold text-navy mb-2">Duration</label>
                            <div className="grid grid-cols-2 gap-2.5 max-w-xs">
                              {[3, 2, 7].map((n) => {
                                const available = (selectedAirlineEntry.byNights?.[n] || []).some((o) => o.flightAvailable);
                                return (
                                  <Tile
                                    key={n}
                                    selected={nights === n}
                                    onClick={() => available && selectNights(n)}
                                    className={`py-3 text-center font-bold text-navy text-[15px] ${!available ? 'opacity-40 pointer-events-none' : ''}`}
                                    dotSize="w-4 h-4"
                                  >
                                    {n}<span className="block text-[11px] font-medium text-slate-400 -mt-0.5">nights{!available ? ' · no flights' : ''}</span>
                                  </Tile>
                                );
                              })}
                            </div>
                          </>
                        )}

                        {/* Flight time, shown once airport + date + airline (+ duration) are all picked */}
                        {!isSelfBooked && nights && availableFlightOptions.length === 1 && (
                          <p className="mt-3 text-[13px] text-slate-500">
                            <span className="font-bold text-navy">Flight time:</span> {availableFlightOptions[0].times || 'to be confirmed'}
                          </p>
                        )}
                        {!isSelfBooked && nights && availableFlightOptions.length > 1 && (
                          <div className="mt-3">
                            <label className="block text-[13.5px] font-bold text-navy mb-2">Flight time</label>
                            <div className="grid gap-2 max-w-sm">
                              {availableFlightOptions.map((opt, i) => (
                                <Tile key={i} selected={flightOptionIdx === i} onClick={() => setFlightOptionIdx(i)} className="px-4 py-2.5 text-[14px] font-semibold text-navy" dotSize="w-4 h-4">
                                  {opt.times || `Option ${i + 1}`}
                                </Tile>
                              ))}
                            </div>
                          </div>
                        )}
                        {isSelfBooked && (
                          <p className="text-[13px] text-slate-500 flex items-start gap-2">
                            <span className="font-semibold text-navy">{selectedAirlineEntry.record.route}</span> · {selectedAirlineEntry.record.airline} · {selectedAirlineEntry.record.times}
                          </p>
                        )}
                      </div>
                    )}

                    {/* Custom/midweek dates — no matrix data to pull an airline or
                        flight time from, so both are always "to be confirmed";
                        MST still books the flight, priced manually like any other. */}
                    {flightDateMode === 'custom' && airport && (
                      <div className="grid sm:grid-cols-2 gap-5 mt-5">
                        <div>
                          <label className="block text-[13.5px] font-bold text-navy mb-2">Departure date</label>
                          <input
                            type="date" min={new Date().toISOString().split('T')[0]}
                            value={customDate} onChange={(e) => setCustomDate(e.target.value)}
                            className="field w-full border border-border rounded-xl px-4 py-3 text-[15px] bg-surface text-navy"
                          />
                        </div>
                        <div>
                          <label className="block text-[13.5px] font-bold text-navy mb-2">Duration</label>
                          <div className="grid grid-cols-3 gap-2.5">
                            {[2, 3, 4].map((n) => (
                              <Tile key={n} selected={customNights === n} onClick={() => setCustomNights(n)} className="py-3 text-center font-bold text-navy text-[15px]" dotSize="w-4 h-4">
                                {n}<span className="block text-[11px] font-medium text-slate-400 -mt-0.5">nights</span>
                              </Tile>
                            ))}
                          </div>
                        </div>
                        <p className="text-[12px] text-slate-400 sm:col-span-2">Flight time will be confirmed once {brandConfig.name} books your dates.</p>
                      </div>
                    )}

                    {/* Manual flight price — staff-only field, never shown on the
                        external page. Shared by both weekend and custom dates. */}
                    {!isExternal && checkInDate && activeNights && (
                      <div className="mt-4 max-w-xs">
                        <label className="block text-[13.5px] font-bold text-navy mb-2">Flight price (£ pp)</label>
                        <input
                          type="number" min="0" step="0.01" value={flightPricePP}
                          onChange={(e) => setFlightPricePP(e.target.value)}
                          placeholder="Leave blank if not yet known"
                          className="field w-full border border-border rounded-xl px-4 py-2.5 text-[15px] bg-surface text-navy"
                        />
                        <p className="text-[11.5px] text-slate-400 mt-1.5">Only add this once you know a real fare — it's never auto-filled, but adding it here includes it in the total below.</p>
                      </div>
                    )}
                  </div>
                )}

                {mode === 'own' && (
                  <div className="grid sm:grid-cols-2 gap-5 mb-7">
                    <div>
                      <label className="block text-[13.5px] font-bold text-navy mb-2">Check-in date</label>
                      <input
                        type="date" min={new Date().toISOString().split('T')[0]}
                        value={ownDate} onChange={(e) => setOwnDate(e.target.value)}
                        className="field w-full border border-border rounded-xl px-4 py-3 text-[15px] bg-surface text-navy"
                      />
                    </div>
                    <div>
                      <label className="block text-[13.5px] font-bold text-navy mb-2">Duration</label>
                      <div className="grid grid-cols-2 gap-2.5">
                        {[3, 2, 7].map((n) => (
                          <Tile key={n} selected={ownNights === n} onClick={() => setOwnNights(n)} className="py-3 text-center font-bold text-navy text-[15px]" dotSize="w-4 h-4">
                            {n}<span className="block text-[11px] font-medium text-slate-400 -mt-0.5">nights</span>
                          </Tile>
                        ))}
                      </div>
                    </div>
                  </div>
                )}

                <FixturesPanel matchdays={fixturesForDate} />

                {/* Hotel/board/room aren't customer-picked on the external tool —
                    MST assigns accommodation once the request lands in the CRM.
                    hotelKey/boardType/roomType still auto-select the first
                    available combination behind the scenes so calc.hotelAvailable
                    stays true and pricing/step-validation keep working. */}
                {!isExternal && (
                  <div className="rounded-2xl border border-border bg-white p-6 mt-5 mb-7">
                    <label className="block text-[13.5px] font-bold text-navy mb-1">Where you'll stay</label>
                    <p className="text-[12.5px] text-slate-400 mb-4">
                      {checkInDate ? 'Pricing updates for your selected date.' : 'Pick your dates above to see live pricing.'}
                    </p>
                    <div className="grid sm:grid-cols-2 gap-3 mb-5">
                      {availableHotels.map((h) => (
                        <Tile key={h.key} selected={hotelKey === h.key} onClick={() => selectHotel(h.key)} className="p-4" dotSize="w-4 h-4">
                          <div className="font-bold text-navy text-[14.5px] leading-tight pr-6">{h.name}</div>
                        </Tile>
                      ))}
                    </div>
                    {checkInDate && availableHotels.length === 0 && (
                      <p className="text-[12.5px] text-slate-500 mb-5">No hotels have pricing for this date — try a different date.</p>
                    )}

                    {hotel.boardTypes.length > 1 && (
                      <div className="mb-5">
                        <label className="block text-[13px] font-bold text-navy mb-2">Board type</label>
                        <div className="grid grid-cols-2 gap-2.5 max-w-xs">
                          {hotel.boardTypes.map((bt) => (
                            <Tile key={bt} selected={boardType === bt} onClick={() => setBoardType(bt)} className="py-2.5 text-center font-semibold text-navy text-[13.5px]" dotSize="w-4 h-4">
                              {BOARD_TYPE_LABELS[bt]}
                            </Tile>
                          ))}
                        </div>
                      </div>
                    )}

                    <label className="block text-[13px] font-bold text-navy mb-2">Room type</label>
                    <div className="grid grid-cols-2 gap-2.5 max-w-sm">
                      {hotel.roomTypes.map((rt) => {
                        const rate = checkInDate ? getHotelRate(hotelKey, boardType, rt, checkInDate, activeNights, totalTravellers) : null;
                        const disabled = !checkInDate || !rate.available || !fxRates;
                        const gbpEach = rate?.available && fxRates
                          ? toGBP(rate.isTotal ? rate.perPerson : rate.perPerson * (activeNights || 0), 'EUR', fxRates) * markupMultiplier
                          : null;
                        return (
                          <Tile
                            key={rt}
                            selected={roomType === rt}
                            onClick={() => !disabled && setRoomType(rt)}
                            className={`p-3.5 ${disabled ? 'opacity-40 pointer-events-none' : ''}`}
                            dotSize="w-4 h-4"
                          >
                            <div className="font-bold text-navy text-[14.5px] leading-tight pr-6">{ROOM_TYPE_LABELS[rt]}</div>
                            <div className="text-[12px] text-slate-400 mt-1">
                              {!checkInDate ? 'Select dates first'
                                : !rate.available ? rate.reason
                                : gbpEach != null ? `${gbp(gbpEach)}${rate.isTotal ? ' pp total' : ' pp for the stay'}`
                                : 'Pricing unavailable — try again shortly'}
                            </div>
                          </Tile>
                        );
                      })}
                    </div>
                    {checkInDate && !calc.hotelAvailable && (
                      <p className="mt-3 text-[12.5px] text-dr">{calc.hotelUnavailableReason || 'Not available for this room type on your selected date — try another hotel or room type.'}</p>
                    )}
                  </div>
                )}

                <div className="rounded-2xl border border-border bg-white p-7 max-w-md">
                  <label className="block text-[13.5px] font-bold text-navy mb-2">Total participants</label>
                  <input
                    type="number" min="1" max="200" value={group}
                    onChange={(e) => setGroup(e.target.value)}
                    className="field w-full border border-border rounded-xl px-4 py-4 text-[22px] font-extrabold text-navy bg-surface"
                  />
                  <p className="text-[13px] text-slate-400 mt-3">
                    Every 15 paying travellers bring one free place (usually a coach or teacher) — that's <span className="font-semibold text-navy">{calc.freeCount} free place{calc.freeCount === 1 ? '' : 's'}</span> and <span className="font-semibold text-navy">{calc.payingCount} paying</span> based on your group size, priced at our <span className="font-semibold text-navy">{ratioLabel}</span> group rate. Final numbers are confirmed by {brandConfig.name} on your firm quote.
                  </p>
                </div>
              </div>
            )}

            {/* STEP 3 — Extras */}
            {step === 3 && (
              <div>
                <StepHeader n={3} title="Add your extras" sub="Your package includes hotel &amp; full board, transport and a stadium tour — everything below is priced into your estimate." />
                <div className="rounded-xl bg-amber-light border border-ba/30 px-5 py-4 flex items-start gap-3 mb-5">
                  <svg className="w-5 h-5 text-ba flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zM11.25 15h.008v.008h-.008V15z" /></svg>
                  <p className="text-[13.5px] text-slate-600 leading-relaxed">
                    <span className="font-bold text-navy">Fixture dates are provisional.</span> La Liga doesn't confirm the exact day of a match until around two weeks before kick-off, so specific fixtures are subject to change.
                  </p>
                </div>

                <div className="rounded-2xl border border-border bg-white p-6 mb-5">
                  <label className="block text-[13.5px] font-bold text-navy mb-1">Fixtures &amp; training sessions</label>
                  <p className="text-[12.5px] text-slate-400 mb-4">
                    {isExternal ? 'Priced per fixture and per training session, per squad.' : `${gbp(calc.fixtureUnit)} per fixture and ${gbp(calc.trainingUnit)} per training session, per squad.`}
                  </p>
                  <div className="grid sm:grid-cols-3 gap-4">
                    <div>
                      <label className="block text-[12px] font-semibold text-slate-500 mb-1.5">Squads</label>
                      <input
                        type="number" min="1" max="10" value={numberOfSquads}
                        onChange={(e) => setNumberOfSquads(e.target.value)}
                        className="field w-full border border-border rounded-xl px-3 py-2.5 text-[16px] font-bold text-navy bg-surface"
                      />
                    </div>
                    <QtyStepper label="Fixtures" value={fixtureQty} onChange={setFixtureQty} min={0} />
                    <QtyStepper label="Training sessions" value={trainingQty} onChange={setTrainingQty} min={0} />
                  </div>
                  {!isExternal && (
                    <p className="mt-3 text-[12.5px] text-slate-500">{gbp(calc.fixturesTotal + calc.trainingTotal)} total ({calc.payingCount > 0 ? gbp((calc.fixturesTotal + calc.trainingTotal) / calc.payingCount) : gbp(0)} pp)</p>
                  )}
                </div>

                <div className="grid sm:grid-cols-2 gap-3.5">
                  {OPTIONAL_EXTRA_CATEGORIES.map((name) => {
                    const item = pcItem(name);
                    if (!item) return null;
                    const sessionBased = SESSION_BASED_EXTRA_CATEGORIES.includes(name);
                    const selected = extraIds.has(name);
                    const sessions = name === 'Padel' ? padelSessions : name === 'Yoga' ? yogaSessions : 1;
                    const setSessions = name === 'Padel' ? setPadelSessions : name === 'Yoga' ? setYogaSessions : null;
                    return (
                      // Session-based extras (Padel/Yoga) need a qty stepper alongside the
                      // toggle — Tile renders as a <button>, so the stepper's own buttons
                      // live as a sibling here, not nested inside it (invalid HTML otherwise).
                      <div key={name}>
                        <Tile selected={selected} onClick={() => setExtraIds((prev) => { const next = new Set(prev); next.has(name) ? next.delete(name) : next.add(name); return next; })} className="p-5">
                          <h3 className="font-extrabold text-navy text-[15.5px] leading-tight pr-8">{name}</h3>
                          {!isExternal && (
                            <p className="text-[12px] text-slate-400 mt-1">{gbp(Number(item.costToCustomer) || 0)} {sessionBased ? 'per session' : 'pp'}</p>
                          )}
                        </Tile>
                        {sessionBased && selected && (
                          <div className="mt-2">
                            <QtyStepper label={`${name} sessions`} value={sessions} onChange={setSessions} min={1} />
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* STEP 4 — Request quote */}
            {step === 4 && (
              <div>
                {!submitted ? (
                  <>
                    <StepHeader n={4} title="Request your firm quote" sub={isExternal ? "Happy with what you've built? Send it to us and we'll confirm a final price — usually within one working day." : "Happy with your estimate? Send it to our team and we'll confirm a final price — usually within one working day."} />
                    {!isExternal && (
                      <div className="rounded-2xl border border-border bg-white shadow-card p-6 sm:p-7 mb-5">
                        <label className="block text-[13.5px] font-bold text-navy mb-2">Who's requesting this quote?</label>
                        <div className="grid grid-cols-2 gap-3">
                          <Tile selected={submitterType === 'customer'} onClick={() => setSubmitterType('customer')} className="py-3 text-center font-semibold text-[14px]" dotSize="w-4 h-4">
                            I'm the customer
                          </Tile>
                          <Tile selected={submitterType === 'staff'} onClick={() => setSubmitterType('staff')} className="py-3 text-center font-semibold text-[14px]" dotSize="w-4 h-4">
                            I'm an MST sales agent
                          </Tile>
                        </div>
                      </div>
                    )}

                    {!isExternal && submitterType === 'staff' && (
                      <ContactAttachPanel
                        currentUser={currentUser}
                        contactMode={contactMode} setContactMode={setContactMode}
                        customers={customers} customerSearch={customerSearch} setCustomerSearch={setCustomerSearch}
                        showCustomerDropdown={showCustomerDropdown} setShowCustomerDropdown={setShowCustomerDropdown}
                        selectedCustomer={selectedCustomer} setSelectedCustomer={setSelectedCustomer}
                        newCustomer={newCustomer} setNewCustomer={setNewCustomer}
                        creatingCustomer={creatingCustomer} customerError={customerError}
                        onCreateCustomer={handleCreateCustomer}
                        onSelectCustomer={prefillFromCustomer}
                      />
                    )}

                    <form onSubmit={handleSubmit} className="rounded-2xl border border-border bg-white shadow-card p-6 sm:p-7 space-y-5">
                      {/* Honeypot — hidden from real visitors, bots that fill every field trip it */}
                      <input
                        type="text" name="website" tabIndex="-1" autoComplete="off"
                        value={honeypot} onChange={(e) => setHoneypot(e.target.value)}
                        style={{ position: 'absolute', left: '-9999px', width: '1px', height: '1px', opacity: 0 }}
                        aria-hidden="true"
                      />
                      {!isExternal && submitterType === 'staff' && (
                        <div className="grid sm:grid-cols-2 gap-5">
                          <div>
                            <label className="block text-[13.5px] font-bold text-navy mb-1.5">Your name (MST staff) <span className="text-ba">*</span></label>
                            <input type="text" value={form.salesAgentName} onChange={(e) => setForm((f) => ({ ...f, salesAgentName: e.target.value }))} placeholder="Your full name" className="field w-full border border-border rounded-xl px-4 py-3 text-[15px] bg-surface" />
                          </div>
                          <div>
                            <label className="block text-[13.5px] font-bold text-navy mb-1.5">CRM flow</label>
                            <select value={staffFlow} onChange={(e) => setStaffFlow(e.target.value)} className="field w-full border border-border rounded-xl px-4 py-3 text-[15px] bg-surface">
                              {flows.map((f) => <option key={f} value={f}>{f}</option>)}
                            </select>
                          </div>
                        </div>
                      )}
                      <div className="grid sm:grid-cols-2 gap-5">
                        <div>
                          <label className="block text-[13.5px] font-bold text-navy mb-1.5">Customer full name <span className="text-ba">*</span></label>
                          <input type="text" value={form.fullName} onChange={(e) => setForm((f) => ({ ...f, fullName: e.target.value }))} placeholder="Full name" className="field w-full border border-border rounded-xl px-4 py-3 text-[15px] bg-surface" />
                        </div>
                        <div>
                          <label className="block text-[13.5px] font-bold text-navy mb-1.5">Email address <span className="text-ba">*</span></label>
                          <input type="email" value={form.email} onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))} placeholder="you@example.com" className="field w-full border border-border rounded-xl px-4 py-3 text-[15px] bg-surface" />
                        </div>
                      </div>
                      <div className="grid sm:grid-cols-2 gap-5">
                        <div>
                          <label className="block text-[13.5px] font-bold text-navy mb-1.5">Group / school name <span className="text-ba">*</span></label>
                          <input type="text" value={form.groupName} onChange={(e) => setForm((f) => ({ ...f, groupName: e.target.value }))} placeholder="e.g. St Kevin's FC U16s" className="field w-full border border-border rounded-xl px-4 py-3 text-[15px] bg-surface" />
                        </div>
                        <div>
                          <label className="block text-[13.5px] font-bold text-navy mb-1.5">Phone <span className="text-ba">*</span></label>
                          <input type="tel" value={form.phone} onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))} placeholder="+44 7700 000000" className="field w-full border border-border rounded-xl px-4 py-3 text-[15px] bg-surface" />
                        </div>
                      </div>
                      {error && <p className="text-sm text-dr bg-red-light rounded-lg px-4 py-3">{error}</p>}
                      <button id="quote-submit-mobile" type="submit" disabled={submitting} className="w-full bg-navy text-white font-bold py-4 rounded-xl hover:bg-pb transition-colors text-[15px] disabled:opacity-60">
                        {submitting ? 'Sending…' : 'Send my quote request'}
                      </button>
                      <p className="text-[12.5px] text-slate-400 text-center">By submitting you agree to be contacted by {brandConfig.name} about your enquiry. {PRICE_DISCLAIMER}</p>
                    </form>
                  </>
                ) : (
                  <div className="text-center rounded-2xl border border-border bg-white shadow-card p-10 sm:p-12">
                    <div className="w-16 h-16 rounded-full mx-auto mb-6 flex items-center justify-center bg-green-light">
                      <Check className="w-8 h-8 text-sg" />
                    </div>
                    <h3 className="text-2xl font-extrabold text-navy mb-3">Thanks — your request is on its way!</h3>
                    <p className="text-slate-500 leading-relaxed text-[15px] max-w-md mx-auto">
                      {isExternal
                        ? <>Your request has been sent to the {brandConfig.name} team. We'll be in touch shortly with your quote. Keep an eye on your inbox — a confirmation is coming to <span className="font-semibold text-navy">{form.email}</span>.</>
                        : <>Your quote request has been sent to our team. We'll be in touch shortly with your firm price. Keep an eye on your inbox — a confirmation is coming to <span className="font-semibold text-navy">{form.email}</span>.</>}
                    </p>
                    {!isExternal && (
                      <div className="mt-7 inline-flex flex-col items-center">
                        <div className="rounded-xl bg-surface border border-border px-6 py-4 text-center">
                          <div className="text-[11px] font-bold uppercase text-pb mb-1">Your estimate</div>
                          <div className="font-extrabold text-ba text-[28px] leading-none">{totalDisplay}</div>
                          <div className="text-[12.5px] text-slate-400 mt-1">{calc.totalTravellers} travellers · {calc.perPerson > 0 ? perPersonDisplay + ' pp' : ''}</div>
                        </div>
                        <p className="text-[12px] text-slate-400 mt-4 max-w-sm">{PRICE_DISCLAIMER}</p>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* NAV */}
            {!(step === MAX_STEP && submitted) && (
              <div className="flex items-center justify-between gap-4 mt-8">
                <button
                  type="button" onClick={goBack} disabled={step === 1}
                  className="inline-flex items-center gap-2 text-slate-500 font-semibold text-[15px] px-5 py-3 rounded-xl border border-border hover:bg-white hover:text-navy transition-all disabled:opacity-0 disabled:pointer-events-none"
                >
                  ← Back
                </button>
                {step < MAX_STEP && (
                  <button
                    type="button" onClick={goNext} disabled={!stepValid(step)}
                    className="inline-flex items-center gap-2 bg-ba text-navy font-bold text-[15px] px-7 py-3.5 rounded-xl hover:brightness-105 transition-all shadow-amber disabled:opacity-40 disabled:cursor-not-allowed disabled:shadow-none"
                  >
                    Continue →
                  </button>
                )}
              </div>
            )}
          </div>

          {/* RIGHT: sticky price summary — internal only. External shows no
              pricing at all; the customer just submits and hears back from
              the team with a proper quote. */}
          {!isExternal && (
            <aside className="hidden lg:block sticky top-24">
              <div className="rounded-2xl border border-border bg-white shadow-card overflow-hidden">
                <div className="relative h-28 bg-navy">
                  <div className="absolute inset-0 bg-gradient-to-t from-navy via-navy/40 to-transparent" />
                  <div className="absolute left-5 bottom-3 text-white">
                    <div className="text-[10px] font-bold uppercase text-ba">Your estimate</div>
                    <div className="font-extrabold text-[15px] leading-tight">Costa Blanca football tour</div>
                  </div>
                </div>
                <div className="p-5">
                  <SummaryLines />
                  <div className="my-4 h-px bg-border" />
                  <div className="flex items-end justify-between mb-1">
                    <span className="text-[13px] font-semibold text-slate-500">Per person</span>
                    <span className="font-bold text-navy text-[17px]">{perPersonDisplay}</span>
                  </div>
                  <div className="flex items-end justify-between rounded-xl px-3 py-3 -mx-1">
                    <div>
                      <span className="text-[13px] font-bold text-navy">Total estimate</span>
                      <div className="text-[11.5px] text-slate-400">{calc.totalTravellers > 0 && calc.perPerson > 0 ? `${calc.totalTravellers} travellers · ${calc.freeCount} free` : ''}</div>
                    </div>
                    <span className="font-extrabold text-ba text-[26px] leading-none">{totalDisplay}</span>
                  </div>
                  <p className="mt-4 text-[11.5px] text-slate-400 leading-relaxed">{PRICE_DISCLAIMER}</p>
                </div>
              </div>
              {step < MAX_STEP && (
                <button onClick={() => stepValidUpTo(MAX_STEP - 1) ? setStep(MAX_STEP) : null} className="w-full mt-3 bg-navy text-white font-bold py-3.5 rounded-xl hover:bg-pb transition-colors text-[14px]">
                  Request this quote
                </button>
              )}
            </aside>
          )}
        </div>
      </main>

      {/* MOBILE BOTTOM BAR — pricing hidden entirely on the external page */}
      <div className="lg:hidden fixed bottom-0 inset-x-0 z-40 bg-white border-t border-border" style={{ boxShadow: '0 -8px 24px -12px rgba(13,34,64,0.18)' }}>
        {!isExternal && (
          <button onClick={() => setMobileBreakdownOpen((v) => !v)} className="w-full flex items-center justify-between px-5 py-2 border-b border-border/70">
            <span className="text-[12px] font-semibold text-slate-500">View breakdown</span>
            <span className={`text-slate-400 transition-transform ${mobileBreakdownOpen ? 'rotate-180' : ''}`}>▾</span>
          </button>
        )}
        {!isExternal && mobileBreakdownOpen && (
          <div className="px-5 py-3 border-b border-border/70 max-h-[40vh] overflow-auto">
            <SummaryLines />
            <p className="text-[11px] text-slate-400 mt-3 leading-relaxed">{PRICE_DISCLAIMER}</p>
          </div>
        )}
        <div className="px-5 py-3 flex items-center gap-3">
          <div className="flex-1 min-w-0">
            {isExternal ? (
              <div className="text-[12px] font-semibold text-slate-500">Step {step} of {MAX_STEP}</div>
            ) : (
              <>
                <div className="text-[11px] text-slate-400 leading-none mb-1">{perPersonDisplay} pp · estimate</div>
                <div className="font-extrabold text-ba text-[22px] leading-none truncate">{totalDisplay}</div>
              </>
            )}
          </div>
          <button
            onClick={() => step < MAX_STEP ? goNext() : document.getElementById('quote-submit-mobile')?.click()}
            disabled={step < MAX_STEP && !stepValid(step)}
            className="bg-ba text-navy font-bold text-[14px] px-6 py-3 rounded-xl shadow-amber whitespace-nowrap disabled:opacity-40"
          >
            {submitted ? 'Sent ✓' : step === MAX_STEP ? 'Send request' : 'Continue'}
          </button>
        </div>
      </div>
      <div className="lg:hidden h-32" />
    </div>
  );
}
